import React from 'react';
import {
  Box,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import { HelpLink } from '../../../../support/campaigns';
import { GhostButton } from './primitives';

/**
 * Editor for an optional `{ label, url }[]` help-link list, authored on a campaign or a step and
 * persisted to `metadata.helpLinks`. Replaces the frontier runner's hard-coded `trainingVideoUrl`:
 * whatever the author adds here renders as a help control in the runner, and an empty list renders
 * nothing at all (the control never shows a placeholder). A purely controlled sub-form — it owns no
 * state; the parent form binds `value`/`onChange`.
 *
 * Rows with a blank url are dropped on change so a half-typed row never reaches the runner; the
 * label falls back to the url when left empty.
 */
export function LinkListEditor(props: {
  value: HelpLink[];
  onChange: (links: HelpLink[]) => void;
  disabled?: boolean;
  /** Short helper line describing what these links are for in this context. */
  help?: string;
}): JSX.Element {
  const { value, onChange, disabled, help } = props;
  const rows = value ?? [];

  const patch = (index: number, next: Partial<HelpLink>) => {
    const updated = rows.map((r, i) => (i === index ? { ...r, ...next } : r));
    onChange(updated);
  };
  const add = () => onChange([...rows, { label: '', url: '' }]);
  const removeAt = (index: number) => onChange(rows.filter((_, i) => i !== index));

  return (
    <Stack spacing={1.25}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Help &amp; resource links
      </Typography>
      {help && (
        <Typography variant="caption" color="text.secondary">
          {help}
        </Typography>
      )}
      {rows.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          None — no help control will render. Add a link to surface one.
        </Typography>
      )}
      {rows.map((row, index) => (
        <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField
            label="Label"
            size="small"
            value={row.label}
            disabled={disabled}
            onChange={(e) => patch(index, { label: e.target.value })}
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="URL"
            size="small"
            fullWidth
            value={row.url}
            disabled={disabled}
            onChange={(e) => patch(index, { url: e.target.value })}
          />
          {!disabled && (
            <IconButton
              aria-label="Remove link"
              size="small"
              onClick={() => removeAt(index)}
              sx={{ flexShrink: 0 }}
            >
              ✕
            </IconButton>
          )}
        </Stack>
      ))}
      {!disabled && (
        <Box>
          <GhostButton onClick={add}>+ Add link</GhostButton>
        </Box>
      )}
    </Stack>
  );
}

export default LinkListEditor;
